import React from "react";
import { styled } from "@mui/material/styles";
import { Box, useTheme, Typography, useMediaQuery } from "@mui/material/";

import Container from "../Layout/Container";
import AcceptanceMarks from "../AcceptanceMarks";
import t__ from "../../util/lang/util";
import intro from "../../util/lang/intro";
import marqueeImgDefault from "./images/lifestyle-image@2x.png";
import marqueeImgMobileDefault from "./images/orb@2x.png";
import UnionPayLogo from "../Icons/unionpay@2x.png";

const PREFIX = "IntroMarquee";

const classes = {
  marquee: `${PREFIX}-marquee`,
  marqueeHeader: `${PREFIX}-marqueeHeader`,
  marqueeImg: `${PREFIX}-marqueeImg`,
  marqueeContent: `${PREFIX}-marqueeContent`,
  travelBox: `${PREFIX}-travelBox`,
  marqueeMobileImg: `${PREFIX}-marqueeMobileImg`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled("div")(({ theme }) => ({
  [`& .${classes.marquee}`]: {
    alignItems: "flex-start",
    flexDirection: "column",
    height: 700,
    backgroundColor: theme.palette.background.seaFoam,
    color: theme.palette.common.white,
    position: "relative",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      height: "calc(100vw / 2.5)",
      alignItems: "center",
      maxHeight: "360px",
    },
  },

  [`& .${classes.marqueeHeader}`]: {
    zIndex: 1,
    position: "relative",
    lineHeight: "46px",
    marginBottom: "24px",
    marginTop: "12px",
    [theme.breakpoints.down("lg")]: {
      marginTop: "24px",
      fontSize: "24px",
      lineHeight: 1.42,
    },
  },

  [`& .${classes.marqueeImg}`]: {
    height: "100%",
    position: "relative",
    right: -1,
    marginLeft: -1,
  },

  [`& .${classes.marqueeContent}`]: {
    backgroundColor: theme.palette.secondary.light,
    height: 165,
    width: "100%",
    textAlign: "center",
    "&:after": {
      position: "absolute",
      zIndex: 0,
      bottom: 250,
      left: 0,
      content: `" "`,
      display: "block",
      height: 300,
      width: "100%",
      borderBottomRightRadius: "50vw",
      borderBottomLeftRadius: "50vw",
      backgroundColor: theme.palette.secondary.light,
    },
    [theme.breakpoints.up("md")]: {
      flex: 1,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: "250px",
      borderBottomRightRadius: "250px",
      height: "100%",
      width: "auto",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
      paddingLeft: theme.spacing(7),
      "&:after": {
        display: "none",
      },
    },
  },

  [`& .${classes.travelBox}`]: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-start",
    },
  },

  [`& .${classes.marqueeMobileImg}`]: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    height: 250,
    background: `url(${marqueeImgMobileDefault}) 0 0 no-repeat`,
    backgroundSize: "cover",
    backgroundPosition: "top center",
  },
}));

const IntroMarquee = () => {
  const theme = useTheme();

  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  return (
    <Root>
      <Box
        display="flex"
        width="100%"
        className={classes.marquee}
        data-aos="fade-left"
        data-aos-delay="400"
        data-aos-duration="500"
        data-aos-once="true"
      >
        <Container
          style={{
            background: "transparent",
            transform: isMediumScreen ? "translate(-50%, -50%)" : "",
          }}
          position="absolute"
          top={isMediumScreen ? "50%" : theme.spacing(15)}
          left={isMediumScreen ? "50%" : 0}
          zIndex={2}
        >
          <Box paddingLeft={isMediumScreen ? theme.spacing(20) : ""}>
            <Box className={classes.travelBox}>
              <img src={UnionPayLogo} alt="Union Pay" width={77} height={48} />
            </Box>
            <Box
              maxWidth={{ xs: "320px", sm: "420px", md: "450px", lg: "600px" }}
              marginX={isMediumScreen ? "0" : "auto"}
            >
              <Typography
                component="h1"
                variant="subtitle1"
                className={classes.marqueeHeader}
                align={isMediumScreen ? "left" : "center"}
              >
                {t__(theme.lang, intro, "marqueeHeader")}
              </Typography>
              <Typography
                component="p"
                variant={isLgScreen ? "h3" : "body1"}
                align={isMediumScreen ? "left" : "center"}
              >
                {t__(theme.lang, intro, "marqueeSubhead")}
              </Typography>
            </Box>
          </Box>
        </Container>

        <Box
          className={classes.marqueeContent}
          style={{
            color: theme.palette.common.white,
          }}
        ></Box>
        {isMediumScreen ? (
          <Box className={classes.marqueeImg}>
            <img
              src={marqueeImgDefault}
              className={classes.marqueeImg}
              alt=""
            />
          </Box>
        ) : (
          <Box className={classes.marqueeMobileImg} />
        )}
      </Box>
      <div
        data-aos="fade-up"
        data-aos-delay="700"
        data-aos-duration="500"
        data-aos-once="true"
      >
        <Box
          bgcolor={"#efefef"}
          py={10}
          display="flex"
          flexDirection={isMediumScreen ? "row" : "column"}
          justifyContent="center"
          alignItems="center"
        >
          <Typography component="p" variant="h4" align="center">
            <b>{t__(theme.lang, intro, "marqueeLook")}</b>
          </Typography>
          <Box marginLeft={isMediumScreen ? 8 : 0} mt={isMediumScreen ? 0 : 3}>
            <AcceptanceMarks marks={["UP", "DN", "DCI"]} variant="small" />
          </Box>
        </Box>
      </div>
    </Root>
  );
};

export default IntroMarquee;
